import React, { createContext, useCallback, useEffect, useState } from "react";
import { DICT, DEFAULT_LANG, LANGUAGES, type SupportedLang } from "../lib/i18n-dict";

interface I18nContextValue {
  lang: SupportedLang;
  setLang: (lang: SupportedLang) => void;
  t: (key: string) => string;
  languages: typeof LANGUAGES;
}

export type { I18nContextValue };

export const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const STORAGE_KEY = "rc-acs-lang";

function isSupportedLang(value: unknown): value is SupportedLang {
  return value === "pt-BR" || value === "pt-PT" || value === "es" || value === "en";
}

function getInitialLang(): SupportedLang {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isSupportedLang(stored)) return stored;
  } catch {
    // storage unavailable
  }
  return DEFAULT_LANG;
}

function safeAttrName(attr: string): boolean {
  return (
    attr.startsWith("aria-")
    || attr === "title"
    || attr === "placeholder"
    || attr === "alt"
    || attr === "label"
  );
}

function translateTextNode(el: Element, dict: Record<string, string>) {
  const key = el.getAttribute("data-i18n");
  if (key && dict[key] && el.children.length === 0) {
    el.textContent = dict[key];
  }
}

function translateAttrNode(el: Element, dict: Record<string, string>) {
  const raw = el.getAttribute("data-i18n-attr");
  if (!raw) return;
  try {
    const map: unknown = JSON.parse(raw);
    if (map === null || typeof map !== "object" || Array.isArray(map)) return;
    for (const [attr, key] of Object.entries(map as Record<string, unknown>)) {
      if (typeof key !== "string") continue;
      if (safeAttrName(attr) && dict[key]) {
        el.setAttribute(attr, dict[key]);
      }
    }
  } catch {
    // invalid JSON — ignore
  }
}

function scanAndTranslate(root: HTMLElement, dict: Record<string, string>) {
  if (root.matches("[data-i18n]")) translateTextNode(root, dict);
  root.querySelectorAll("[data-i18n]").forEach((el) => translateTextNode(el, dict));

  if (root.matches("[data-i18n-attr]")) translateAttrNode(root, dict);
  root.querySelectorAll("[data-i18n-attr]").forEach((el) => translateAttrNode(el, dict));
}

function resolveTranslation(key: string, lang: SupportedLang): string {
  const dict = DICT[lang];
  if (dict?.[key]) return dict[key];
  return DICT[DEFAULT_LANG]?.[key] ?? key;
}

interface I18nProviderProps {
  children: React.ReactNode;
}

let observer: MutationObserver | null = null;

function startDomBridge(lang: SupportedLang) {
  if (typeof document === "undefined") return;
  const dict = DICT[lang];
  if (!dict) return;
  scanAndTranslate(document.body, dict);
  if (observer) observer.disconnect();
  observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) {
          scanAndTranslate(node, dict);
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function stopDomBridge() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [lang, setLangState] = useState<SupportedLang>(getInitialLang);

  const setLang = useCallback((next: SupportedLang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // storage unavailable
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
    startDomBridge(lang);
    return () => stopDomBridge();
  }, [lang]);

  const t = useCallback(
    (key: string): string => resolveTranslation(key, lang),
    [lang],
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t, languages: LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}
